import { esc } from '../esc';
import { firstSupportedCurrency, methodSupportsCurrency, needsBilling } from '../methods';
import type { DepositMethod, DepositMethodId } from '../types';

export interface PaymentMethodPickerOptions {
  methods: DepositMethod[];
  currency: string;
  selected?: DepositMethodId | null;
  /** true/false when known; undefined = unknown (no notice shown). */
  billingComplete?: boolean;
  billingHref?: string;
  iconBase?: string;
  onSelect?: (method: DepositMethod) => void;
  /** Called when the chosen method needs another currency than the current one (after confirmation). */
  onCurrencySwitch?: (currency: string) => void;
  /**
   * Picking a method that cannot charge the current currency asks first
   * ("BLIK charges in PLN — switch from EUR?"). `false` switches silently;
   * a function replaces the built-in inline prompt (return true to switch).
   */
  confirmCurrencySwitch?: false | ((from: string, to: string, method: DepositMethod) => boolean | Promise<boolean>);
  /** Show methods that exist but are not enabled (greyed out, not selectable). */
  showDisabled?: boolean;
}

export interface PaymentMethodPickerHandle {
  destroy(): void;
  getSelected(): DepositMethod | null;
  getCurrency(): string;
  setCurrency(currency: string): void;
  setBillingComplete(complete: boolean | undefined): void;
}

export function mountPaymentMethodPicker(el: HTMLElement, opts: PaymentMethodPickerOptions): PaymentMethodPickerHandle {
  const iconBase = (opts.iconBase ?? '/payment-methods/').replace(/\/?$/, '/');
  const billingHref = opts.billingHref ?? '/settings#billing-address';
  let currency = opts.currency;
  let selectedId: DepositMethodId | null = opts.selected ?? null;
  let billingComplete = opts.billingComplete;
  /** A method waiting for the player to confirm a currency switch. */
  let pending: { method: DepositMethod; to: string } | null = null;
  const visible = opts.methods.filter((m) => m.enabled !== false || opts.showDisabled);

  const selected = () => opts.methods.find((m) => m.id === selectedId) ?? null;

  const render = () => {
    const sel = selected();
    const items = visible.map((m) => {
      const enabled = m.enabled !== false;
      const ok = methodSupportsCurrency(m, currency);
      const isSel = m.id === selectedId;
      const note = !enabled ? '<span class="block text-xs text-text-dim">Unavailable</span>'
        : ok ? '' : `<span class="block text-xs text-text-dim">${esc(m.currencies.join(', '))} only</span>`;
      return `
        <button type="button" data-method="${esc(m.id)}" role="radio" aria-checked="${isSel}" ${enabled ? '' : 'disabled aria-disabled="true"'}
          class="flex items-center gap-3 w-full min-h-[44px] px-3 py-2 text-left bg-surface border rounded touch-manipulation transition-colors duration-[120ms] ${isSel ? 'border-gold shadow-glow' : 'border-border'} ${ok && enabled ? '' : 'opacity-70'}">
          <img src="${esc(iconBase + m.icon)}" alt="" width="32" height="32" class="w-8 h-8 object-contain shrink-0" loading="lazy" />
          <span class="flex-1 min-w-0"><span class="block text-text">${esc(m.label)}</span>${note}</span>
        </button>`;
    }).join('');
    const showNotice = !!sel && needsBilling(sel) && billingComplete === false;
    const confirm = pending ? `
      <div data-role="currency-confirm" class="mt-3 px-3 py-2.5 bg-surface border border-gold/40 rounded text-[13px] text-text" role="alertdialog">
        <p>${esc(pending.method.label)} charges in ${esc(pending.to)}. Switch currency from ${esc(currency)} to ${esc(pending.to)}?</p>
        <div class="mt-2 flex gap-2">
          <button type="button" data-action="confirm-switch" class="flex-1 min-h-[44px] px-3 bg-gold text-bg font-bold rounded touch-manipulation">Switch to ${esc(pending.to)}</button>
          <button type="button" data-action="cancel-switch" class="flex-1 min-h-[44px] px-3 border border-border rounded touch-manipulation">Cancel</button>
        </div>
      </div>` : '';
    el.innerHTML = `
      <div class="flex flex-col gap-2" role="radiogroup" aria-label="Payment method">${items}</div>
      ${confirm}
      ${showNotice ? `<p data-role="billing-notice" class="mt-3 px-3 py-2.5 bg-gold/10 border border-gold/40 rounded text-[13px] text-text">
        This method needs a complete billing address. <a href="${esc(billingHref)}" class="underline">Add billing address</a></p>` : ''}`;
  };

  const choose = (m: DepositMethod, switchTo: string | null) => {
    selectedId = m.id;
    pending = null;
    if (switchTo) {
      currency = switchTo;
      opts.onCurrencySwitch?.(switchTo);
    }
    render();
    opts.onSelect?.(m);
  };

  const onClick = async (e: Event) => {
    const target = e.target as HTMLElement;
    const action = (target.closest('[data-action]') as HTMLElement | null)?.dataset.action;
    if (action === 'confirm-switch' && pending) { choose(pending.method, pending.to); return; }
    if (action === 'cancel-switch') { pending = null; render(); return; }

    const btn = target.closest('[data-method]') as HTMLElement | null;
    if (!btn) return;
    const m = opts.methods.find((x) => x.id === btn.dataset.method);
    if (!m || m.enabled === false) return;
    if (methodSupportsCurrency(m, currency)) { choose(m, null); return; }
    const next = firstSupportedCurrency(m, null);
    if (!next) return;
    if (opts.confirmCurrencySwitch === false) { choose(m, next); return; }
    if (typeof opts.confirmCurrencySwitch === 'function') {
      if (await opts.confirmCurrencySwitch(currency, next, m)) choose(m, next);
      return;
    }
    pending = { method: m, to: next };
    render();
  };

  el.addEventListener('click', onClick);
  render();

  return {
    destroy() { el.removeEventListener('click', onClick); el.innerHTML = ''; },
    getSelected: selected,
    getCurrency: () => currency,
    setCurrency(c) { currency = c; pending = null; render(); },
    setBillingComplete(c) { billingComplete = c; render(); },
  };
}
